import tippy, { Instance, Props } from 'tippy.js';

import { isMobile, isTablet } from '../../../core/helpers/device';
import { sanitizeHTML } from '../../../core/helpers/text-sanitizer';

const TOOLTIP_PROP: unique symbol = Symbol('pp-tooltip');
const TOOLTIP_ATTR = 'data-pp-tooltip';

export type TTooltipElement = HTMLElement & {
  _tippy?: Instance;
  [TOOLTIP_PROP]?: Tooltip;
};

export function destroyTooltipsInSubtree(root: HTMLElement | null | undefined): void {
  if (!root) {
    return;
  }

  const descendants: NodeListOf<HTMLElement> = root.querySelectorAll(`[${TOOLTIP_ATTR}]`);
  const tagged: HTMLElement[] = root.hasAttribute(TOOLTIP_ATTR)
    ? [root, ...Array.from(descendants)]
    : Array.from(descendants);

  for (const element of tagged) {
    const tooltip: Tooltip | undefined = (element as TTooltipElement)[TOOLTIP_PROP];

    if (tooltip) {
      tooltip.removeTooltip();
    }
  }
}

export type TTooltipParams = {
  options?: Partial<Props>;
  title?: string;
  mobileTooltip?: boolean;
  trimTooltip?: boolean;
  childTooltip?: boolean;
  element: TTooltipElement;
  trimElement?: TTooltipElement;
  interactive?: boolean;
};

export type TTooltipUpdateParams = Partial<Omit<TTooltipParams, 'element'>>;

type TBatchEntry = {
  tooltip: Tooltip;
  onReady?: () => void;
};

const TOOLTIP_BATCH_DEBOUNCE_MS = 300;
// Hard ceiling on how long the debounce can keep getting reset. Without
// this, sustained activity (scrolling, fast typing) would defer the flush
// forever.
const TOOLTIP_BATCH_MAX_WAIT_MS = 1000;

let batchQueue: TBatchEntry[] = [];
let batchTimerId: ReturnType<typeof setTimeout> | null = null;
let maxWaitTimerId: ReturnType<typeof setTimeout> | null = null;

function flushTooltipBatch(): void {
  if (batchTimerId !== null) {
    clearTimeout(batchTimerId);
    batchTimerId = null;
  }
  if (maxWaitTimerId !== null) {
    clearTimeout(maxWaitTimerId);
    maxWaitTimerId = null;
  }

  const entries = batchQueue;
  batchQueue = [];

  for (const entry of entries) {
    if (entry.tooltip.isCancelled) {
      continue;
    }

    entry.tooltip.applyTooltip();

    if (entry.onReady) {
      entry.onReady();
    }
  }
}

function scheduleBatch(entry: TBatchEntry): void {
  batchQueue.push(entry);

  Zone.root.run(() => {
    if (batchTimerId !== null) {
      clearTimeout(batchTimerId);
    }
    batchTimerId = setTimeout(flushTooltipBatch, TOOLTIP_BATCH_DEBOUNCE_MS);

    if (maxWaitTimerId === null) {
      maxWaitTimerId = setTimeout(flushTooltipBatch, TOOLTIP_BATCH_MAX_WAIT_MS);
    }
  });
}

export default class Tooltip {
  public _element: TTooltipElement;
  public _host: TTooltipElement;
  isTrimmed = true;
  isCancelled = false;
  private options: Partial<Props>;
  title: string;
  private mobileTooltip: boolean;
  private childTooltip: boolean;
  private trimTooltip: boolean;
  private trimElement: TTooltipElement;
  private elementResolved = false;

  commonOption = {
    arrow: true,
    inertia: true,
    animation: 'fade',
    theme: 'pinpoint',
    allowHTML: true,
    interactive: false,
    // duration: [0, 1000000], // Enable for testing so tooltip don't dissapear
  };

  constructor({
    options = {},
    title = '',
    mobileTooltip = false,
    trimTooltip = false,
    childTooltip = false,
    element,
    trimElement = null,
    interactive = false,
  }: TTooltipParams) {
    this._element = element;
    this._host = element;
    (this._host as TTooltipElement)[TOOLTIP_PROP] = this;
    this._host.setAttribute(TOOLTIP_ATTR, '');

    this.options = options;
    this.rejectNonStringContentOption();
    this.title = title ? sanitizeHTML(title) : undefined;
    this.mobileTooltip = mobileTooltip;
    this.trimTooltip = trimTooltip;
    this.childTooltip = childTooltip;
    this.trimElement = trimElement ? trimElement : element;
    this.commonOption.interactive = interactive;
  }

  // We only sanitise string content. Anything else tippy accepts —
  // Element, DocumentFragment, or a lazy function — would slip past
  // sanitisation and, combined with allowHTML: true, could render
  // attacker-controlled markup. No caller needs those shapes, so reject
  // them up front rather than carry the risk.
  private rejectNonStringContentOption(): void {
    if (this.options?.content !== undefined && typeof this.options.content !== 'string') {
      delete this.options.content;
    }
  }

  filterTippyOptions(options: any): Partial<Props> {
    const validKeys = new Set([
      'allowHTML',
      'animation',
      'arrow',
      'content',
      'delay',
      'duration',
      'hideOnClick',
      'interactive',
      'maxWidth',
      'offset',
      'placement',
      'theme',
      'trigger',
      'onShow',
      'onHide',
    ]);

    return Object.fromEntries(
      Object.entries(options).filter(([key]) => validKeys.has(key)),
    ) as Partial<Props>;
  }

  createTooltip(onReady?: () => void): void {
    const mobile = isMobile();
    const tablet = isTablet();

    if ((mobile || tablet) && !this.mobileTooltip) {
      return;
    }

    if (this.options) {
      this.options = this.filterTippyOptions(this.options);
    }

    scheduleBatch({ tooltip: this, onReady });
  }

  applyTooltip(): void {
    if (!this._host.isConnected) {
      this.removeTooltip();

      return;
    }

    this.updateTrimmedState();
    this.resolveTippyTarget();

    if (this.trimTooltip && !this.isTrimmed && this._element._tippy) {
      this._element._tippy.destroy();
    }

    if (!this.trimTooltip || this.isTrimmed) {
      if (this.title || this.options?.content) {
        if (!('html' in this.options) || Object.keys(this.options).length === 0) {
          const contentTitle = this.title ? { content: this.title } : {};
          const options = Object.assign({}, this.commonOption, this.options, contentTitle);

          this._element._tippy?.destroy();
          tippy(this._element, options);
        } else {
          const htmlId = (this.options as Record<string, unknown>)['html'] as string;
          const template = document.getElementById(htmlId);
          delete (this.options as Record<string, unknown>)['html'];

          if (!template) {
            return;
          }

          this.options['content'] = template.innerHTML;
          const options = Object.assign({}, this.commonOption, this.options);

          this._element._tippy?.destroy();
          tippy(this._element, options);
        }
      }
    }
  }

  update(params: TTooltipUpdateParams): void {
    if (this.isCancelled) {
      return;
    }

    if (!this._host.isConnected) {
      this.removeTooltip();

      return;
    }

    const wasTrimmed: boolean = this.isTrimmed;

    if (params.title !== undefined) {
      this.title = params.title ? sanitizeHTML(params.title) : undefined;
    }

    if (params.options !== undefined) {
      this.options = params.options;
      this.rejectNonStringContentOption();
      this.options = this.filterTippyOptions(this.options);
    }

    if (params.mobileTooltip !== undefined) {
      this.mobileTooltip = params.mobileTooltip;
    }

    if (params.trimTooltip !== undefined) {
      this.trimTooltip = params.trimTooltip;
    }

    if (params.childTooltip !== undefined) {
      this.childTooltip = params.childTooltip;
    }

    if (params.interactive !== undefined) {
      this.commonOption.interactive = params.interactive;
    }

    if (params.trimElement !== undefined) {
      this.trimElement = params.trimElement;
    }

    const hasLiveTippy = Boolean(this._element._tippy);

    if (!hasLiveTippy) {
      return;
    }

    this.updateTrimmedState();
    this.resolveTippyTarget();

    if (this.trimTooltip && !this.isTrimmed) {
      this._element._tippy.destroy();

      return;
    }

    if (this.trimTooltip && this.isTrimmed && !wasTrimmed) {
      this.createTooltip();

      return;
    }

    if (!this.title && !this.options?.content) {
      this._element._tippy.disable();

      return;
    }

    this._element._tippy.enable();

    const content: Props['content'] = this.title ?? this.options?.content ?? '';

    this._element._tippy.setProps({
      ...this.options,
      content,
    });
  }

  removeTooltip(): void {
    this.isCancelled = true;

    if (this._host._tippy) {
      this._host._tippy.destroy();
    }

    if (this._element !== this._host && this._element._tippy) {
      this._element._tippy.destroy();
    }

    delete (this._host as TTooltipElement)[TOOLTIP_PROP];
    this._host.removeAttribute(TOOLTIP_ATTR);
  }

  updateTrimmedState(): void {
    // On a disconnected element offsetWidth and scrollWidth both report 0, which would falsely tell us the text is not truncated
    const isTrimElementConnected: boolean = this.trimElement?.isConnected ?? false;

    if (!this.trimTooltip || !isTrimElementConnected) {
      return;
    }

    if (this.childTooltip && this.trimElement.children[0]) {
      const child = this.trimElement.children[0] as HTMLElement;

      this.isTrimmed = this.trimElement.offsetWidth - child.offsetWidth - 26 < 0;

      return;
    }

    if (!this.childTooltip) {
      this.isTrimmed = this.trimElement.offsetWidth < this.trimElement.scrollWidth;
    }
  }

  resolveTippyTarget(): void {
    if (this.elementResolved) {
      return;
    }

    if (this._element.children.length > 0 && this.isNgHostElement(this._element)) {
      const child = this._element.children[0] as HTMLElement;

      if (child.isConnected) {
        this._element = child;
        this.elementResolved = true;
      }

      return;
    }

    this.elementResolved = true;
  }

  private isNgHostElement(element: HTMLElement): boolean {
    const attrs = element.attributes;

    for (let i = 0; i < attrs.length; i++) {
      if (attrs[i].name.indexOf('_nghost') > -1) {
        return true;
      }
    }

    return false;
  }
}
